import * as React from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { IMantleRichText, IMantleCtaButton } from '~domain/entities/Node';
import { MantleCtaButton } from './fields/MantleCtaButton';
import { MantleRichText } from './fields/MantleRichText';

export const COMPONENT_ID = 'additional-content';

export enum ContentPosition {
    ABOVE = 'above',
    BELOW = 'below'
}

export interface IAdditionalContent {
    position: ContentPosition;
    layoutName: string;
    eyebrow: IMantleRichText;
    header: IMantleRichText;
    content: IMantleRichText;
    primaryCtaButton?: IMantleCtaButton;
    secondaryCtaButton?: IMantleCtaButton;
    findationCtaButton?: IMantleCtaButton;
    findationOnClick?(): void;
    hideMobile: string;
    hidePc: string;
    textAlignment: string;
}

export const AdditionalContent = observer(function AdditionalContent({
    position,
    layoutName,
    eyebrow,
    header,
    content,
    primaryCtaButton,
    secondaryCtaButton,
    findationCtaButton,
    findationOnClick,
    hideMobile,
    hidePc,
    textAlignment
}: IAdditionalContent) {
    const testId = `${COMPONENT_ID}-${position}`;

    const additionalContentBlockClassNames = classnames(
        `elc-${layoutName}-content-${position}-block`,
        `js-${layoutName}-content-${position}-block`,
        `${layoutName}__content-wrapper`,
        'content-block__content-wrapper',
        'content-block__content',
        'align-center',
        `${layoutName}__content-${position}-block`,
        hideMobile && 'mobile-flex-hidden',
        hidePc && 'pc-hidden'
    );

    const additionalContentClassNames = classnames(
        `elc-${layoutName}-content-${position}`,
        `js-${layoutName}-content-${position}`,
        `${layoutName}__content`,
        'content-block',
        'content-block__content',
        'justify-center',
        'horizontal-align-center',
        `${layoutName}__content-${position}`,
        textAlignment
    );

    const eyebrowClassNames = classnames(
        `elc-${layoutName}-eyebrow`,
        `js-${layoutName}-eyebrow`,
        'content-block__line',
        'content-block__line--eyebrow',
        `${layoutName}__eyebrow`
    );

    const headerClassNames = classnames(
        `elc-${layoutName}-header`,
        `js-${layoutName}-header`,
        'content-block__line',
        'content-block__line--header',
        `${layoutName}__header`
    );

    const contentClassNames = classnames(
        `elc-${layoutName}-content`,
        `js-${layoutName}-content`,
        'content-block__line',
        'content-block__line--content',
        `${layoutName}__content`
    );

    const linksWrapperClassNames = classnames(
        `elc-${layoutName}__links-wrapper`,
        `js-${layoutName}__links-wrapper`,
        'content-block__links',
        `${layoutName}__links-wrapper`
    );

    const linkClassNames = classnames(
        `elc-${layoutName}-link`,
        `js-${layoutName}-link`,
        'content-block__line',
        'content-block__line--link',
        `${layoutName}__link`
    );

    const findationCtaWrapperClassNames = classnames(
        `elc-${layoutName}__findation-cta-wrapper`,
        `js-${layoutName}__findation-cta-wrapper`,
        'content-block__findation-cta',
        `${layoutName}__findation-cta-wrapper`
    );

    const findationCtaClassNames = classnames(
        `elc-${layoutName}-findation-cta`,
        `js-${layoutName}-findation-cta`,
        'content-block__line',
        'content-block__line--findation-cta',
        `${layoutName}__findation-cta`
    );

    const hasCtaButtons = primaryCtaButton?.text || secondaryCtaButton?.text;
    const shouldShowContent =
        eyebrow?.html || header?.html || content?.html || findationCtaButton?.text || hasCtaButtons;

    if (!shouldShowContent) {
        return null;
    }

    return (
        <div data-test-id={testId} className={additionalContentBlockClassNames}>
            <div className={additionalContentClassNames}>
                <MantleRichText {...eyebrow} className={eyebrowClassNames} />
                <MantleRichText {...header} className={headerClassNames} />
                <MantleRichText {...content} className={contentClassNames} />
                {hasCtaButtons && (
                    <div className={linksWrapperClassNames}>
                        {primaryCtaButton && (
                            <MantleCtaButton {...primaryCtaButton} className={linkClassNames} />
                        )}
                        {secondaryCtaButton && (
                            <MantleCtaButton {...secondaryCtaButton} className={linkClassNames} />
                        )}
                    </div>
                )}
                {findationCtaButton?.text && (
                    <div className={findationCtaWrapperClassNames}>
                        <MantleCtaButton
                            {...findationCtaButton}
                            className={findationCtaClassNames}
                            onClick={findationOnClick}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});
